import type { Difficulty } from './ai/types';
import { BLACK, type Color, WHITE } from './types';

export type GameMode = 'two_player' | 'vs_computer';

export interface GameSettings {
  mode: GameMode;
  /** Difficulty when mode === 'vs_computer'. */
  difficulty: Difficulty;
  /**
   * Which color the human plays when mode === 'vs_computer'. 'random' means
   * pick at game-start time.
   */
  humanColor: Color | 'random';
}

export const DEFAULT_SETTINGS: GameSettings = {
  mode: 'two_player',
  difficulty: 3,
  humanColor: BLACK,
};

const SETTINGS_KEY = 'konane:v1:settings';

export function loadSettings(): GameSettings {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_SETTINGS };
    const obj = parsed as Record<string, unknown>;
    const mode =
      obj.mode === 'vs_computer' || obj.mode === 'two_player' ? obj.mode : DEFAULT_SETTINGS.mode;
    const difficulty =
      typeof obj.difficulty === 'number' && obj.difficulty >= 1 && obj.difficulty <= 5
        ? (obj.difficulty as Difficulty)
        : DEFAULT_SETTINGS.difficulty;
    const humanColor =
      obj.humanColor === BLACK || obj.humanColor === WHITE || obj.humanColor === 'random'
        ? obj.humanColor
        : DEFAULT_SETTINGS.humanColor;
    return { mode, difficulty, humanColor };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: GameSettings): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    // ignore
  }
}

/** Resolve `humanColor` to a concrete color for a new game. */
export function resolveHumanColor(s: GameSettings): Color {
  if (s.humanColor === 'random') {
    return Math.random() < 0.5 ? BLACK : WHITE;
  }
  return s.humanColor;
}

/** Returns true if it's currently the AI's turn given the settings + state. */
export function isAITurn(
  mode: GameMode,
  humanColor: Color | null,
  turn: Color,
  isOver: boolean,
): boolean {
  if (mode !== 'vs_computer') return false;
  if (isOver) return false;
  return humanColor !== turn;
}
